import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";

import Button from "components/Button";
import Input from "components/Input";
import mailroomService, { mailroomQueryKeys } from "api/mailroomService";
import parcelService from "api/parcelService";
import { EParcelStoreKind, IParcelLookup } from "dto/Mailroom/IMailroom";
import { ENotificationType, notification } from "utils/notification";

import styles from "./styles.module.scss";

// Panel admina biura podawczego: domyślny dekretujący + zarządzanie słownikami
// (rodzaje załączników, formy doręczenia), biurami podawczymi i składami.

const LookupEditor = ({
  title,
  hint,
  items,
  onSave,
  onRemove,
}: {
  title: string;
  hint?: string;
  items: IParcelLookup[];
  onSave: (name: string, id?: number) => void;
  onRemove: (id: number) => void;
}) => {
  const [newName, setNewName] = useState("");

  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>{title}</div>
      {hint && <div className={styles.hint}>{hint}</div>}
      <div className={styles.list}>
        {items.map((it) => (
          <div key={it.Id} className={styles.row}>
            <div className={styles.rowName}>{it.Name}</div>
            {it.IsSystem ? (
              <span className={styles.badge}>systemowa</span>
            ) : (
              <Button text="Usuń" onClick={() => onRemove(it.Id)} />
            )}
          </div>
        ))}
      </div>
      <div className={styles.addRow}>
        <div className={styles.grow}>
          <Input
            label="Nowa pozycja"
            value={newName}
            placeholder="Nazwa"
            onChange={setNewName}
          />
        </div>
        <Button
          text="Dodaj"
          onClick={() => {
            if (!newName.trim()) return;
            onSave(newName.trim());
            setNewName("");
          }}
        />
      </div>
    </div>
  );
};

const MailroomTab = () => {
  const queryClient = useQueryClient();
  const workspaceId =
    localStorage.getItem("workspaceId") ??
    sessionStorage.getItem("workspaceId") ??
    undefined;

  const { data: attData } = mailroomService.useGetAttachmentTypes();
  const { data: kindData } = mailroomService.useGetDeliveryKinds();
  const { data: officeData } = mailroomService.useGetRegistryOffices();
  const { data: storeData } = mailroomService.useGetParcelStores();
  const { data: settingsData } = mailroomService.useGetSettings();
  const { data: initData } = parcelService.useInitParcel(workspaceId ?? undefined);

  const { mutate: saveAttachmentType } = mailroomService.useSaveAttachmentType();
  const { mutate: removeAttachmentType } = mailroomService.useRemoveAttachmentType();
  const { mutate: saveDeliveryKind } = mailroomService.useSaveDeliveryKind();
  const { mutate: removeDeliveryKind } = mailroomService.useRemoveDeliveryKind();
  const { mutate: saveRegistryOffice } = mailroomService.useSaveRegistryOffice();
  const { mutate: removeRegistryOffice } = mailroomService.useRemoveRegistryOffice();
  const { mutate: saveParcelStore } = mailroomService.useSaveParcelStore();
  const { mutate: removeParcelStore } = mailroomService.useRemoveParcelStore();
  const { mutate: updateSettings } = mailroomService.useUpdateSettings();

  const attachmentTypes = attData?.data ?? [];
  const deliveryKinds = kindData?.data ?? [];
  const offices = officeData?.data ?? [];
  const stores = storeData?.data ?? [];
  const receivers = initData?.data?.Receivers ?? [];

  const [defaultReceiver, setDefaultReceiver] = useState<string>("");
  const [defaultOffice, setDefaultOffice] = useState<string>("");
  const [defaultStore, setDefaultStore] = useState<string>("");

  useEffect(() => {
    const s = settingsData?.data;
    if (!s) return;
    setDefaultReceiver(s.DefaultReceiverUserId ?? "");
    setDefaultOffice(s.DefaultRegistryOfficeId ?? "");
    setDefaultStore(s.DefaultParcelStoreId ?? "");
  }, [settingsData]);

  const invalidate = (key: string) => queryClient.invalidateQueries(key);

  const notifyErr = () =>
    notification({
      type: ENotificationType.ERROR,
      title: "Błąd",
      text: "Nie udało się zapisać zmian.",
    });

  const [newOfficeName, setNewOfficeName] = useState("");
  const [newOfficeSymbol, setNewOfficeSymbol] = useState("");
  const [newStoreName, setNewStoreName] = useState("");
  const [newStoreFormat, setNewStoreFormat] = useState("{0}/P/{1:yy}");
  const [newStoreKind, setNewStoreKind] = useState<EParcelStoreKind>(
    EParcelStoreKind.Parcel,
  );
  const [newStoreElectronic, setNewStoreElectronic] = useState(false);
  const [newStoreDefault, setNewStoreDefault] = useState(false);

  const saveDefaults = () => {
    updateSettings(
      {
        DefaultReceiverUserId: defaultReceiver || null,
        DefaultRegistryOfficeId: defaultOffice || null,
        DefaultParcelStoreId: defaultStore || null,
      },
      {
        onSuccess: () => {
          invalidate(mailroomQueryKeys.settings);
          notification({
            type: ENotificationType.SUCCESS,
            title: "Zapisano",
            text: "Ustawienia biura podawczego zapisane.",
          });
        },
        onError: notifyErr,
      },
    );
  };

  return (
    <div className={styles.wrapper}>
      {/* Domyślne ustawienia urzędu */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>Domyślne ustawienia urzędu</div>
        <div className={styles.hint}>
          Domyślny dekretujący jest domyślnym odbiorcą przesyłki rejestrowanej w
          biurze podawczym (można go zmienić w kreatorze).
        </div>
        <div className={styles.twoCol}>
          <FormControl fullWidth size="small">
            <InputLabel>Domyślny dekretujący</InputLabel>
            <Select
              label="Domyślny dekretujący"
              value={defaultReceiver}
              onChange={(e) => setDefaultReceiver(e.target.value as string)}
            >
              <MenuItem value="">—</MenuItem>
              {receivers.map((r) => (
                <MenuItem key={r.Id} value={r.Id}>
                  {r.Label}
                  {r.IsMe ? " (ja)" : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Domyślne biuro podawcze</InputLabel>
            <Select
              label="Domyślne biuro podawcze"
              value={defaultOffice}
              onChange={(e) => setDefaultOffice(e.target.value as string)}
            >
              <MenuItem value="">—</MenuItem>
              {offices.map((o) => (
                <MenuItem key={o.Id} value={o.Id}>
                  {o.Name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <div className={styles.twoCol}>
          <FormControl fullWidth size="small">
            <InputLabel>Domyślny skład</InputLabel>
            <Select
              label="Domyślny skład"
              value={defaultStore}
              onChange={(e) => setDefaultStore(e.target.value as string)}
            >
              <MenuItem value="">—</MenuItem>
              {stores.map((s) => (
                <MenuItem key={s.Id} value={s.Id}>
                  {s.Name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <div />
        </div>
        <div>
          <Button text="Zapisz ustawienia" onClick={saveDefaults} />
        </div>
      </div>

      {/* Słowniki */}
      <LookupEditor
        title="Rodzaje załączników"
        hint="Rodzaj głównego załącznika wyznacza kategorię przesyłki."
        items={attachmentTypes}
        onSave={(name) =>
          saveAttachmentType(
            { Name: name, SortOrder: 0 },
            {
              onSuccess: () => invalidate(mailroomQueryKeys.attachmentTypes),
              onError: notifyErr,
            },
          )
        }
        onRemove={(id) =>
          removeAttachmentType(id, {
            onSuccess: () => invalidate(mailroomQueryKeys.attachmentTypes),
            onError: notifyErr,
          })
        }
      />

      <LookupEditor
        title="Formy doręczenia"
        items={deliveryKinds}
        onSave={(name) =>
          saveDeliveryKind(
            { Name: name, SortOrder: 0 },
            {
              onSuccess: () => invalidate(mailroomQueryKeys.deliveryKinds),
              onError: notifyErr,
            },
          )
        }
        onRemove={(id) =>
          removeDeliveryKind(id, {
            onSuccess: () => invalidate(mailroomQueryKeys.deliveryKinds),
            onError: notifyErr,
          })
        }
      />

      {/* Biura podawcze */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>Biura podawcze</div>
        <div className={styles.hint}>
          Symbol jest prefiksem numeracji przesyłek: „Symbol/kolejny/rok".
        </div>
        <div className={styles.list}>
          {offices.map((o) => (
            <div key={o.Id} className={styles.row}>
              <div className={styles.rowName}>
                {o.Name}
                {o.Symbol ? ` (${o.Symbol})` : ""}
                {o.IsDefault ? " — domyślne" : ""}
              </div>
              <Button
                text="Usuń"
                onClick={() =>
                  removeRegistryOffice(o.Id, {
                    onSuccess: () => invalidate(mailroomQueryKeys.registryOffices),
                    onError: notifyErr,
                  })
                }
              />
            </div>
          ))}
        </div>
        <div className={styles.addRow}>
          <div className={styles.grow}>
            <Input
              label="Nazwa biura"
              value={newOfficeName}
              placeholder="Nazwa"
              onChange={setNewOfficeName}
            />
          </div>
          <div className={styles.grow}>
            <Input
              label="Symbol"
              value={newOfficeSymbol}
              placeholder="Symbol"
              onChange={setNewOfficeSymbol}
            />
          </div>
          <Button
            text="Dodaj"
            onClick={() => {
              if (!newOfficeName.trim()) return;
              saveRegistryOffice(
                {
                  Name: newOfficeName.trim(),
                  Symbol: newOfficeSymbol.trim() || null,
                  IsDefault: false,
                },
                {
                  onSuccess: () => {
                    invalidate(mailroomQueryKeys.registryOffices);
                    setNewOfficeName("");
                    setNewOfficeSymbol("");
                  },
                  onError: notifyErr,
                },
              );
            }}
          />
        </div>
      </div>

      {/* Składy chronologiczne (nadają sygnaturę) */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>Składy chronologiczne</div>
        <div className={styles.hint}>
          Skład nadaje sygnaturę przesyłce wg formatu (np. „{"{0}"}/P/{"{1:yy}"}"
          → 12/P/26). Dobór domyślny wg formy doręczenia (papierowa/elektroniczna).
        </div>
        <div className={styles.list}>
          {stores.map((s) => (
            <div key={s.Id} className={styles.row}>
              <div className={styles.rowName}>
                {s.Name}
                <span className={styles.badge}>{s.NumberFormat}</span>
                <span className={styles.badge}>
                  {s.Kind === EParcelStoreKind.OutgoingParcel
                    ? "wychodzące"
                    : "przychodzące"}
                </span>
                <span className={styles.badge}>
                  {s.IsElectronic ? "elektroniczne" : "papierowe"}
                </span>
                {s.IsDefault ? " — domyślny" : ""}
              </div>
              <Button
                text="Usuń"
                onClick={() =>
                  removeParcelStore(s.Id, {
                    onSuccess: () => invalidate(mailroomQueryKeys.parcelStores),
                    onError: notifyErr,
                  })
                }
              />
            </div>
          ))}
        </div>
        <div className={styles.addRow}>
          <div className={styles.grow}>
            <Input
              label="Nazwa składu"
              value={newStoreName}
              placeholder="Nazwa"
              onChange={setNewStoreName}
            />
          </div>
          <div className={styles.grow}>
            <Input
              label="Format sygnatury"
              value={newStoreFormat}
              placeholder="{0}/P/{1:yy}"
              onChange={setNewStoreFormat}
            />
          </div>
          <FormControl size="small" style={{ minWidth: 150 }}>
            <InputLabel>Rodzaj</InputLabel>
            <Select
              label="Rodzaj"
              value={newStoreKind}
              onChange={(e) => setNewStoreKind(Number(e.target.value) as EParcelStoreKind)}
            >
              <MenuItem value={EParcelStoreKind.Parcel}>Przychodzące</MenuItem>
              <MenuItem value={EParcelStoreKind.OutgoingParcel}>Wychodzące</MenuItem>
            </Select>
          </FormControl>
          <label className={styles.check}>
            <input
              type="checkbox"
              checked={newStoreElectronic}
              onChange={(e) => setNewStoreElectronic(e.target.checked)}
            />
            Elektroniczny
          </label>
          <label className={styles.check}>
            <input
              type="checkbox"
              checked={newStoreDefault}
              onChange={(e) => setNewStoreDefault(e.target.checked)}
            />
            Domyślny
          </label>
          <Button
            text="Dodaj"
            onClick={() => {
              if (!newStoreName.trim() || !newStoreFormat.trim()) return;
              saveParcelStore(
                {
                  Name: newStoreName.trim(),
                  NumberFormat: newStoreFormat.trim(),
                  Kind: newStoreKind,
                  IsElectronic: newStoreElectronic,
                  ContinuousForYear: true,
                  ContinuousForUser: false,
                  IsDefault: newStoreDefault,
                  IsActive: true,
                },
                {
                  onSuccess: () => {
                    invalidate(mailroomQueryKeys.parcelStores);
                    setNewStoreName("");
                    setNewStoreFormat("{0}/P/{1:yy}");
                    setNewStoreElectronic(false);
                    setNewStoreDefault(false);
                  },
                  onError: notifyErr,
                },
              );
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default MailroomTab;
